import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  ArrowLeft, ShieldAlert, MailWarning, AlertCircle, RefreshCw, Loader2,
} from "lucide-react";

const MODULE_TITLE = {
  new_hire: "New Hire Training",
  annual: "Annual Resilience Training",
  critical_incident: "Critical Incident Debrief",
};

export default function AgencyEngagementModule() {
  const { module: moduleType } = useParams();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/agency/engagement/non-completers/${moduleType}`);
      setItems(r.data.non_completers || []);
      // Pre-select all by default
      setSelected(new Set((r.data.non_completers || []).map(m => m.user_id)));
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load non-completers.");
    } finally {
      setLoading(false);
    }
  }, [moduleType]);

  useEffect(() => { load(); }, [load]);

  const toggle = (uid) => {
    const next = new Set(selected);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    setSelected(next);
  };

  const selectAll = () => setSelected(new Set(items.map(m => m.user_id)));
  const clearAll = () => setSelected(new Set());

  const handlePushCritical = () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected).join(",");
    nav(`/agency/critical-incident?targets=${encodeURIComponent(ids)}&source=engagement_${moduleType}`);
  };

  return (
    <AdminLayout>
      <div className="haruki-fade-in">
        <Link to="/agency/engagement"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              data-testid="drilldown-back-link">
          <ArrowLeft className="h-3 w-3" /> Back to engagement
        </Link>
        <div className="mt-3 mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          Engagement · Drill-down
        </div>
        <h1 className="font-heading text-3xl font-bold">
          Pending: {MODULE_TITLE[moduleType] || moduleType}
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Members who have not yet completed this module. Select a subset and push a Critical Incident debrief —
          a structured wrap-around that often re-engages stalled trainees.
        </p>

        {/* States */}
        {loading && (
          <div data-testid="drilldown-loading" className="mt-8 text-sm text-muted-foreground">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading non-completers…
          </div>
        )}

        {error && (
          <div data-testid="drilldown-error" className="mt-8 rounded-md border p-6"
               style={{ borderColor: "hsl(var(--haruki-fire))", background: "hsl(var(--haruki-fire) / 0.06)" }}>
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5" style={{ color: "hsl(var(--haruki-fire))" }} />
              <div className="flex-1">
                <div className="font-heading text-base font-semibold">Couldn't load list</div>
                <p className="mt-1 text-sm text-muted-foreground">{error}</p>
                <Button data-testid="drilldown-retry" onClick={load} className="mt-4" size="sm"
                        style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}>
                  <RefreshCw className="mr-2 h-3 w-3" /> Retry
                </Button>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Empty state */}
            {items.length === 0 ? (
              <Card className="mt-8">
                <CardContent className="p-8 text-center">
                  <div className="font-heading text-base font-semibold">Nothing pending</div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Every eligible member has completed {MODULE_TITLE[moduleType] || moduleType}.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Action bar */}
                <div className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border p-4"
                     style={{ background: "hsl(var(--card))" }}>
                  <div className="text-sm">
                    <span className="font-semibold">{selected.size}</span> of {items.length} selected
                    <span className="mx-2 text-muted-foreground">·</span>
                    <button onClick={selectAll} data-testid="drilldown-select-all"
                            className="text-xs font-semibold text-foreground/60 underline-offset-4 hover:text-foreground hover:underline">Select all</button>
                    <span className="mx-2 text-muted-foreground">·</span>
                    <button onClick={clearAll} data-testid="drilldown-clear-all"
                            className="text-xs font-semibold text-foreground/60 underline-offset-4 hover:text-foreground hover:underline">Clear</button>
                  </div>
                  <Button
                    data-testid="drilldown-push-button"
                    onClick={handlePushCritical}
                    disabled={selected.size === 0}
                    style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
                  >
                    <ShieldAlert className="mr-2 h-4 w-4" />
                    Push critical-incident debrief to selected ({selected.size})
                  </Button>
                </div>

                {/* List */}
                <Card className="mt-4">
                  <CardContent className="p-0">
                    <table className="w-full text-sm">
                      <thead className="border-b border-border text-left text-xs uppercase tracking-widest text-muted-foreground">
                        <tr>
                          <th className="w-12 px-4 py-3"></th>
                          <th className="px-4 py-3">Name</th>
                          <th className="px-4 py-3">Vertical</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3">Enrolled</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map(m => (
                          <tr key={m.user_id}
                              data-testid={`drilldown-row-${m.user_id}`}
                              className="border-b border-border last:border-0">
                            <td className="px-4 py-3">
                              <Checkbox
                                data-testid={`drilldown-check-${m.user_id}`}
                                checked={selected.has(m.user_id)}
                                onCheckedChange={() => toggle(m.user_id)}
                              />
                            </td>
                            <td className="px-4 py-3 font-medium">{m.name}</td>
                            <td className="px-4 py-3 capitalize text-muted-foreground">
                              {m.vertical?.replace(/_/g, " ")}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">
                              {m.started_at ? "In progress" : (m.enrolled_at ? "Not started" : "Not enrolled")}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">{m.enrolled_at?.slice(0, 10) || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>

                {/* Reminder note (drip / email reminder is mocked until Resend wired) */}
                <div className="mt-4 flex items-start gap-2 rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">
                  <MailWarning className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    Soft-reminder email (one-tap) coming once a transactional email provider is connected.
                    For now, the critical-incident push above re-engages the most reliably.
                  </span>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
